import React from 'react';
import { motion } from 'framer-motion';

const TransformationSection = () => {
  const beforeItems = [
    'Security / CNA / Warehouse roles',
    'Constant fear of H1B denial',
    'Sending 500+ resumes with no reply'
  ];

  const afterItems = [
    'Office Job with Standard Hours',
    'Companies willing to file H1B',
    'High Salary ($85k - $120k)'
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-pbi-dark to-black border-b border-gray-800 animate-glow-border">
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            The "African Student" Transformation
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            See how acquiring this single skill transforms your value in the US Job Market.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Before Card */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02, borderColor: 'rgba(239, 68, 68, 0.5)' }}
            className="bg-[#1a1a1a] border border-gray-700 rounded-2xl p-8 relative overflow-hidden group transition-colors"
          >
            <div className="absolute top-0 left-0 w-full h-2 bg-gray-600"></div>
            
            <div className="flex flex-col items-center mb-8">
              <div className="relative mb-4">
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center shadow-inner"
                >
                  <i className="fa-solid fa-user-nurse text-4xl text-gray-500"></i>
                </motion.div>
                <motion.div 
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="absolute -top-2 -right-2 text-red-500 text-2xl font-bold"
                >
                  ?
                </motion.div>
                <motion.div 
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: 0.1 }}
                  className="absolute top-4 -left-4 text-red-500 text-xl font-bold"
                >
                  ?
                </motion.div>
              </div>
              <h3 className="text-xl font-bold text-gray-300">The "Survival Job" Trap</h3>
              <p className="text-sm text-gray-500 mt-1">Doing jobs unrelated to your Degree</p>
            </div>

            <div className="space-y-4">
              {beforeItems.map((item, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 bg-black/40 p-3 rounded border border-gray-800"
                >
                  <i className="fa-solid fa-xmark text-red-500 w-6 text-center"></i>
                  <span className="text-gray-400 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* After Card */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="bg-black border-2 border-pbi-yellow rounded-2xl p-8 relative overflow-hidden shadow-[0_0_30px_rgba(242,200,17,0.15)] transform md:-translate-y-4"
          >
            <div className="absolute top-0 left-0 w-full h-2 bg-pbi-yellow"></div>
            
            <div className="flex flex-col items-center mb-8">
              <div className="relative mb-4">
                <motion.div 
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute inset-0 bg-pbi-yellow rounded-full blur-xl opacity-40"
                />
                <motion.div 
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="w-24 h-24 bg-gray-900 border border-pbi-yellow rounded-full flex items-center justify-center relative z-10"
                >
                  <i className="fa-solid fa-user-tie text-4xl text-pbi-yellow"></i>
                </motion.div>
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  className="absolute -top-4 -right-4 text-pbi-yellow text-2xl"
                >
                  <i className="fa-solid fa-star"></i>
                </motion.div>
                <motion.div 
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-8 -right-8 bg-green-500 text-black text-xs font-bold px-2 py-1 rounded shadow"
                >
                  SPONSORED!
                </motion.div>
              </div>
              <h3 className="text-xl font-bold text-white">The Data Analyst</h3>
              <p className="text-sm text-pbi-yellow mt-1">Value Driven Professional</p>
            </div>

            <div className="space-y-4">
              {afterItems.map((item, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 bg-gray-900 p-3 rounded border border-pbi-yellow/30"
                >
                  <i className="fa-solid fa-check text-green-500 w-6 text-center"></i>
                  <span className="text-white text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TransformationSection;